import LoraRuntimeAPI, {
  LoRAAdapterArtifact,
  ReferenceImageUpload,
  TrainingJob,
} from './loraRuntime';
import GeneratorProfilesAPI, {
  GeneratorProfile,
  GeneratorProfileSpec,
  slugifyName,
} from './generatorProfiles';
import { Character, updateCharacter } from './series';
import { constructMediaUrl } from './helpers';
import { MIN_DATASET_IMAGES } from './httpError';

// ==================== Shared constants ====================

/** The base SDXL checkpoint every trained-identity profile targets. */
export const IDENTITY_BASE_MODEL_ID = 'stabilityai/stable-diffusion-xl-base-1.0';
/** Fallback LoRA strength when the artifact does not recommend one. */
export const DEFAULT_ADAPTER_WEIGHT = 0.75;
/** Subject-token prefix for character-lab profiles seeded from a series character. */
const SUBJECT_TOKEN_PREFIX = 'cg_char';
/** Statuses a training job can rest in. */
const TERMINAL_JOB_STATUSES = new Set(['completed', 'failed', 'cancelled', 'error']);

// ==================== Reference uploads ====================

/** Reads a File into the base64 reference-image upload shape the bridge expects. */
export const fileToReferenceUpload = (file: File): Promise<ReferenceImageUpload> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result : '';
      const dataBase64 = value.includes(',') ? value.split(',').pop() || '' : value;
      resolve({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        data_base64: dataBase64,
      });
    };
    reader.readAsDataURL(file);
  });

/**
 * Pulls a stored reference image (via its raw display URL) and packages it as a
 * base64 reference-image upload so a character-lab profile can be seeded from
 * it. Returns null when there is no usable reference image.
 */
export const referenceUploadFromUrl = async (
  rawUrl: string | undefined,
  name: string
): Promise<ReferenceImageUpload | null> => {
  if (!rawUrl) return null;
  const url = constructMediaUrl(rawUrl);
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const dataBase64 = await blobToBase64(blob);
    if (!dataBase64) return null;
    return {
      filename: `${slugifyName(name) || 'character'}-reference`,
      content_type: blob.type || 'image/png',
      data_base64: dataBase64,
    };
  } catch {
    return null;
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Could not read blob'));
    reader.onload = () => {
      const value = typeof reader.result === 'string' ? reader.result : '';
      resolve(value.includes(',') ? value.split(',').pop() || '' : value);
    };
    reader.readAsDataURL(blob);
  });

// ==================== Character-lab profile + training ====================

/** Builds a deterministic, prompt-safe subject token from a character name. */
export const characterSubjectToken = (character: Character): string => {
  const slug = slugifyName(character.name).replace(/-/g, '_') || 'identity';
  return `${SUBJECT_TOKEN_PREFIX}_${slug}`.slice(0, 48);
};

/**
 * True when the lab profile has a persistent TrainingDataset bound with at least
 * {@link MIN_DATASET_IMAGES} images (studio approvals appended trainable items).
 * Resolved via the profile's `dataset_id`. Inlined against LoraRuntimeAPI (not
 * datasetOrchestration.fetchBoundDataset) to avoid an import cycle, since
 * datasetOrchestration already imports from this module.
 */
const labProfileHasTrainableDataset = async (labProfileId: string): Promise<boolean> => {
  try {
    const profile = await LoraRuntimeAPI.getCharacterProfile(labProfileId);
    if (!profile.dataset_id) return false;
    const dataset = await LoraRuntimeAPI.getDataset(profile.dataset_id);
    return dataset.image_count >= MIN_DATASET_IMAGES;
  } catch {
    return false;
  }
};

/**
 * Kicks off LoRA training for a character-lab profile. When a persistent dataset
 * is bound (studio approvals appended trainable items), trains on it DIRECTLY.
 * Otherwise falls back to building the approved-reference dataset from uploaded
 * references first (min_approved 0 so they count without a candidate pass).
 */
export const startTrainingForLabProfile = async (
  labProfileId: string,
  gpu?: string
): Promise<TrainingJob> => {
  if (!(await labProfileHasTrainableDataset(labProfileId))) {
    await LoraRuntimeAPI.buildCharacterDataset(labProfileId, { min_approved: 0 });
  }
  return LoraRuntimeAPI.trainCharacterLora(labProfileId, {
    base_model_id: IDENTITY_BASE_MODEL_ID,
    rank: 4,
    steps: 24,
    ...(gpu ? { gpu } : {}),
  });
};

/** True once a training job has reached a state it will not leave on its own. */
export const isTerminalJobStatus = (status: string): boolean =>
  TERMINAL_JOB_STATUSES.has(status);

/** Fetches the latest snapshot of a single training job by id, or null if gone. */
export const fetchTrainingJob = async (jobId: string): Promise<TrainingJob | null> => {
  const jobs = await LoraRuntimeAPI.listJobs();
  return jobs.find((job) => job.id === jobId) || null;
};

// ==================== Trained-identity → generator profile ====================

/**
 * Resolves the trained LoRA artifact for a completed job. Prefers the job's own
 * artifact_id, then falls back to the newest matching artifact.
 */
export const findTrainedArtifact = async (
  job: TrainingJob
): Promise<LoRAAdapterArtifact | null> => {
  const artifacts = await LoraRuntimeAPI.listArtifacts();
  if (job.artifact_id) {
    const direct = artifacts.find((artifact) => artifact.id === job.artifact_id);
    if (direct) return direct;
  }
  const byJob = artifacts.filter((artifact) => artifact.training_job_id === job.id);
  return byJob[0] || null;
};

/** Reads a usable trigger/base prompt from an artifact's trigger tokens or metadata. */
const artifactBasePrompt = (artifact: LoRAAdapterArtifact): string => {
  if (artifact.trigger_tokens && artifact.trigger_tokens.length > 0) {
    return artifact.trigger_tokens.join(', ');
  }
  const metadata = artifact.metadata || {};
  const candidate =
    metadata.trigger_word ?? metadata.training_prompt ?? metadata.subject_token ?? metadata.prompt;
  return typeof candidate === 'string' ? candidate : '';
};

/** Assembles the default generator-profile spec for a trained identity artifact. */
export const buildIdentityProfileSpec = (
  artifact: LoRAAdapterArtifact
): GeneratorProfileSpec => ({
  base_model: { source: 'hf', id: IDENTITY_BASE_MODEL_ID },
  adapters: [
    {
      artifact_id: artifact.id,
      weight:
        Number.isFinite(artifact.recommended_strength) && artifact.recommended_strength > 0
          ? artifact.recommended_strength
          : DEFAULT_ADAPTER_WEIGHT,
    },
  ],
  prompt: { base_prompt: artifactBasePrompt(artifact), negative_prompt: '' },
  params: {
    width: 1024,
    height: 1024,
    steps: 30,
    cfg_scale: 7,
    scheduler: '',
    mode: 'txt2img',
    denoise_strength: 0.65,
  },
});

/**
 * Creates AND publishes a default generator profile for a trained identity,
 * then links it onto the character via PUT /api/characters/:id. Returns both the
 * published profile and the updated character so callers can refresh local state.
 */
export const publishIdentityProfileAndLink = async (
  character: Character,
  artifact: LoRAAdapterArtifact
): Promise<{ profile: GeneratorProfile; character: Character }> => {
  const draft = await GeneratorProfilesAPI.createProfile({
    slug: slugifyName(character.name) || `identity-${artifact.id.slice(0, 8)}`,
    name: character.name,
    media_type: 'image',
    spec: buildIdentityProfileSpec(artifact),
  });
  const profile = await GeneratorProfilesAPI.publishProfile(draft.id);
  const updated = await updateCharacter(character, { generator_profile_id: profile.id });
  return { profile, character: updated };
};
